class TyphoonOCRService {
  constructor() {
    this.apiKey = process.env.REACT_APP_TYPHOON_API_KEY;
    this.baseUrl = 'https://api.opentyphoon.ai/v1';
  }

  validateApiKey() {
    if (!this.apiKey) {
      throw new Error('Typhoon API key is not configured. Please check your .env file.');
    }
  }

  async extractTextFromFile(file, pageSelection = 'all') {
    this.validateApiKey();

    try {
      console.log('Starting OCR for file:', file.name, 'Size:', file.size, 'Page:', pageSelection);

      // Prepare params based on page selection
      const model = "typhoon-ocr-preview";
      let params = {
        model: model,
        task_type: "default",
        max_tokens: 16000,
        temperature: 0.1,
        top_p: 0.6,
        repetition_penalty: 1.2
      };

      // Set pages parameter based on selection
      if (pageSelection !== 'all') {
        const pageNum = parseInt(pageSelection);
        if (!isNaN(pageNum)) {
          params.pages = [pageNum];
        }
      } else {
        // For 'all', send first few pages or let API decide
        // Option 1: Send first 10 pages
        // params.pages = [1,2,3,4,5,6,7,8,9,10];
        
        // Option 2: Don't specify pages (let API process all)
        // Leave params.pages undefined
        
        // Option 3: Send a reasonable range
        params.pages = [1,2,3,4,5];
      }

      console.log('OCR params:', params);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('params', JSON.stringify(params));

      const response = await fetch(`${this.baseUrl}/ocr`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          // Don't set Content-Type for FormData, let browser set it
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('OCR API Error:', response.status, errorData);
        throw new Error(`OCR API Error: ${response.status} - ${errorData}`);
      }

      const result = await response.json();
      console.log('OCR Result:', result);

      // Extract text from Typhoon OCR response format
      let totalPages = 0;
      let allPagesData = [];
      
      if (result.results && result.results.length > 0) {
        // Get total pages info
        totalPages = result.total_pages || result.results.length;
        
        // Process each page result
        for (let i = 0; i < result.results.length; i++) {
          const pageResult = result.results[i];
          console.log(`Page ${i + 1} result:`, pageResult);
          
          if (pageResult.success && pageResult.message && 
              pageResult.message.choices && pageResult.message.choices.length > 0) {
            
            const messageContent = pageResult.message.choices[0].message.content;
            let extractedText = messageContent;
            
            try {
              // Try to parse as JSON if it's structured output
              const parsedContent = JSON.parse(messageContent);
              extractedText = parsedContent.natural_text || messageContent;
            } catch (parseError) {
              // Use content as-is if not JSON
              console.log(`Content for page ${i + 1} is not JSON, using as-is`);
            }
            
            allPagesData.push({
              page: pageResult.page_num || (i + 1),
              text: extractedText,
              success: true
            });
          } else {
            console.error(`Error processing page ${i + 1}:`, pageResult.error || 'Unknown error');
            allPagesData.push({
              page: pageResult.page_num || (i + 1),
              text: `ไม่สามารถสกัดข้อความจากหน้านี้ได้: ${pageResult.error || 'Unknown error'}`,
              success: false
            });
          }
        }
        
        // Return based on page selection
        if (pageSelection === 'all') {
          return {
            total_pages: totalPages,
            pages: allPagesData,
            combined_text: allPagesData.map((page, index) => 
              `=== หน้าที่ ${page.page} ===\n${page.text}`
            ).join('\n\n')
          };
        } else {
          // Return specific page
          const pageNum = parseInt(pageSelection);
          const pageData = allPagesData.find(page => page.page === pageNum);
          return pageData ? pageData.text : `ไม่พบข้อมูลสำหรับหน้า ${pageNum}`;
        }
      }
      
      return 'ไม่พบข้อความในเอกสาร';

    } catch (error) {
      console.error('OCR Service Error:', error);
      
      // Return mock data for testing if API fails
      if (error.message.includes('fetch')) {
        const mockPages = [
          {
            page: 1,
            text: `[Mock OCR Data หน้า 1 - API ไม่สามารถเชื่อมต่อได้]

รายงานการฝึกงาน
คณะวิศวกรรมศาสตร์ มหาวิทยาลัยขอนแก่น

ชื่อนิสิต: นางสาวสมใจ ใจดี
รหัสนิสิต: 630610001
สาขาวิชา: วิศวกรรมคอมพิวเตอร์

บทที่ 1 บทนำ
การฝึกงานครั้งนี้ได้ทำให้ผมได้เรียนรู้ทักษะใหม่ๆ มากมาย...`,
            success: true
          },
          {
            page: 2,
            text: `[Mock OCR Data หน้า 2]

บทที่ 2 การพัฒนาระบบ
ระบบที่ข้าพเจ้าได้พัฒนาประกอบด้วยส่วนหลัก 3 ส่วน:

1. ส่วนจัดการข้อมูลนิสิต (Student Management)
2. ส่วนจัดการบริษัท (Company Management)  
3. ส่วนรายงานและสถิติ (Reporting & Analytics)`,
            success: true
          }
        ];
        
        if (pageSelection === 'all') {
          return {
            total_pages: mockPages.length,
            pages: mockPages,
            combined_text: mockPages.map(page => page.text).join('\n\n')
          };
        } else {
          const pageNum = parseInt(pageSelection);
          const pageData = mockPages.find(page => page.page === pageNum);
          return pageData ? pageData.text : `Mock data สำหรับหน้า ${pageNum}`;
        }
      }
      
      throw error;
    }
  }

  async analyzeInternshipReport(text) {
    this.validateApiKey();

    try {
      const prompt = `วิเคราะห์รายงานการฝึกงานต่อไปนี้และจัดหมวดหมู่ข้อมูลเป็น JSON format:

${text}

กรุณาวิเคราะห์และจัดหมวดหมู่เป็น:
1. ทักษะที่เรียนรู้ (แบ่งเป็น technical, soft_skills, specialized)
2. กิจกรรมที่ทำ (แบ่งเป็น regular_work, special_projects, training)
3. ความรู้ที่ได้รับ (แบ่งเป็น academic, practical, experience)
4. ระดับความสามารถ (แบ่งเป็น beginner, intermediate, advanced)
5. ปัญหาที่เกิดขึ้น
6. insights (แบ่งเป็น strengths, improvements, recommendations)

ตอบเป็น JSON format เท่านั้น`;

      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'typhoon-v1.5x-70b-instruct',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 2000,
          temperature: 0.3
        })
      });

      if (!response.ok) {
        throw new Error(`Analysis API Error: ${response.status}`);
      }

      const result = await response.json();
      const analysisText = result.choices[0].message.content;
      
      // Parse JSON from response
      try {
        return JSON.parse(analysisText);
      } catch (parseError) {
        console.error('Failed to parse AI response as JSON:', analysisText);
        throw new Error('AI response is not valid JSON');
      }

    } catch (error) {
      console.error('Analysis Service Error:', error);
      throw error;
    }
  }
}

export default TyphoonOCRService;
